import React, {useState} from "react";
import {useObserver} from "mobx-react-lite";
import stores from "../../store/stores";
import {useTranslation} from "react-i18next";
import {Col, Row, Table, Tabs, Tag} from "antd";
import UserGroupModal from "./modals/UserGroupModal";
import {PlusOutlined} from "@ant-design/icons";
import PermissionPolicyModal from "./modals/PermissionPolicyModal";
import {useFetch} from "../../hooks/useFetch";
import {getLocaleLang} from "../../utils/helpers";
import {CustomButton} from "../_defaultComponents/CustomButton/CustomButton";
import {EmptyMessage} from "../_defaultComponents/EmptyMessage/EmptyMessage";
import {ResponsiveDataTable} from "components/_defaultComponents/ResponsiveDataTable";
import {AuthorizationManagementRow, AuthorizationManagementActions} from "./styles/UserPage.styled";
import {SearchInput} from "../courier/courierPageStyles";

const AuthorizationManagement = () => {
    const {TabPane} = Tabs;

    const [activeKey, setActiveKey] = useState("1");
    const [permissionPolicies, setPermissionPolicies] = useState();
    const [userPermissionPolicies, setUserPermissionPolicies] = useState();
    const [userGroupModalVisibility, setUserGroupModalVisibility] = useState(false);
    const [permissionPolicyModalVisibility, setPermissionPolicyModalVisibility] = useState(false);
    const [recallUserGroup, setRecallUserGroup] = useState(false);
    const [recallPermissionPolicy, setRecallPermissionPolicy] = useState(false);
    const [searchInputText, setSearchInputText] = useState("");
    const [searchText, setSearchText] = useState("");

    const [modalTitle, setModalTitle] = useState("");
    const {t, i18n} = useTranslation(["userAuthorization"]);

    const [userGroupLoading, userGroup, ,] = useFetch({
        defaultDataValue: {},
        fetchFn: () => stores.authorizationStore.listUserGroups({searchText}),
        dependencies: [recallUserGroup, searchText]
    });
    const [permissionPoliciesDataLoading, permissionPoliciesData, ,] = useFetch({
        defaultDataValue: {},
        fetchFn: () => stores.authorizationStore.listPermissionPolicies({searchText}),
        dependencies: [recallPermissionPolicy, searchText]
    });

    const changeTab = value => {
        setActiveKey(value);
    };

    const handleUserGroupCreateModalPress = () => {
        setUserPermissionPolicies();
        setModalTitle(t("modals.userGroupCreateModal.label", {ns: "userAuthorization"}));
        setUserGroupModalVisibility(true);
    };
    const handleUserGroupDetailModalPress = item => {
        setModalTitle(t("modals.userGroupDetailModal.label", {ns: "userAuthorization"}));
        setUserGroupModalVisibility(true);
        setUserPermissionPolicies(item);
    };

    const handlePermissionPolicyModalPress = () => {
        setPermissionPolicies();
        setModalTitle(t("modals.permissionPolicyCreateModal.label", {ns: "userAuthorization"}));
        setPermissionPolicyModalVisibility(true);
    };

    const handlePermissionPolicyDetailModalPress = item => {
        setModalTitle(t("modals.permissionPolicyDetailModal.label", {ns: "userAuthorization"}));
        setPermissionPolicyModalVisibility(true);
        setPermissionPolicies(item);
    };

    const onCancelUserGroupModal = () => {
        setUserGroupModalVisibility(false);
        setUserPermissionPolicies();
    };

    return useObserver(() => (
        <React.Fragment>
            <UserGroupModal
                visibility={userGroupModalVisibility}
                modalTitle={modalTitle}
                onCancel={onCancelUserGroupModal}
                detail={userPermissionPolicies}
                allPermissionPolicies={permissionPoliciesData?.rows}
                setRecallUserGroup={setRecallUserGroup}
            />
            <PermissionPolicyModal
                visibility={permissionPolicyModalVisibility}
                modalTitle={modalTitle}
                onCancel={() => setPermissionPolicyModalVisibility(false)}
                detail={permissionPolicies}
                allPermissionPoliciesData={permissionPoliciesData?.rows}
                setRecallPermissionPolicy={setRecallPermissionPolicy}
            />

            <AuthorizationManagementRow>
                <Col>
                    <Tabs activeKey={activeKey} onChange={changeTab}>
                        <TabPane tab={t("tabs.userGroup", {ns: "userAuthorization"})} key="1" />
                        <TabPane
                            tab={t("tabs.permissionPolicies", {ns: "userAuthorization"})}
                            key="2"
                        />
                    </Tabs>
                </Col>
                <Col>
                    <AuthorizationManagementActions>
                        <SearchInput
                            placeholder={t("filters.search", {ns: "common"})}
                            allowClear
                            value={searchInputText}
                            onChange={event => {
                                setSearchInputText(event.target.value);
                                if (!event.target.value) setSearchText("");
                            }}
                            onSearch={value => setSearchText(value)}
                        />
                        {activeKey === "1" ? (
                            <CustomButton
                                onClick={handleUserGroupCreateModalPress}
                                icon={<PlusOutlined />}
                            >
                                {t("components.newUserGroupButton.label", {
                                    ns: "userAuthorization"
                                })}
                            </CustomButton>
                        ) : (
                            <CustomButton
                                onClick={handlePermissionPolicyModalPress}
                                icon={<PlusOutlined />}
                            >
                                {t("components.newPermissionPolicyButton.label", {
                                    ns: "userAuthorization"
                                })}
                            </CustomButton>
                        )}
                    </AuthorizationManagementActions>
                </Col>
            </AuthorizationManagementRow>
            {activeKey === "1" ? (
                <ResponsiveDataTable
                    rowKey="_id"
                    loading={userGroupLoading}
                    dataSource={userGroup?.rows}
                    locale={{
                        emptyText: <EmptyMessage />
                    }}
                >
                    <Table.Column
                        dataIndex="name"
                        title={t("components.userGroupList.fields.name", {ns: "userAuthorization"})}
                        render={name => <p>{name[getLocaleLang(i18n)]}</p>}
                        onCell={() => ({
                            "data-label": t("components.userGroupList.fields.name", {
                                ns: "userAuthorization"
                            })
                        })}
                    />

                    <Table.Column
                        align="right"
                        title={t("actions.action", {ns: "common"})}
                        render={item => (
                            <a onClick={() => handleUserGroupDetailModalPress(item)}>
                                {t("actions.detail", {ns: "common"})}
                            </a>
                        )}
                        onCell={() => ({
                            "data-label": t("actions.action", {ns: "common"})
                        })}
                    />
                </ResponsiveDataTable>
            ) : (
                <ResponsiveDataTable
                    rowKey="_id"
                    loading={permissionPoliciesDataLoading}
                    dataSource={permissionPoliciesData?.rows}
                    locale={{
                        emptyText: <EmptyMessage />
                    }}
                >
                    <Table.Column
                        dataIndex="name"
                        title={t("components.permissionPolicyList.fields.name", {
                            ns: "userAuthorization"
                        })}
                        render={name => <p>{name[getLocaleLang(i18n)]}</p>}
                        onCell={() => ({
                            "data-label": t("components.permissionPolicyList.fields.name", {
                                ns: "userAuthorization"
                            })
                        })}
                    />

                    <Table.Column
                        dataIndex="description"
                        title={t("components.permissionPolicyList.fields.description", {
                            ns: "userAuthorization"
                        })}
                        render={description => (
                            <span data-testid="description">
                                {description[getLocaleLang(i18n)]}
                            </span>
                        )}
                        onCell={() => ({
                            "data-label": t("components.permissionPolicyList.fields.description", {
                                ns: "userAuthorization"
                            })
                        })}
                    />
                    <Table.Column
                        align="center"
                        dataIndex="tags"
                        title={t("components.permissionPolicyList.fields.tags", {
                            ns: "userAuthorization"
                        })}
                        render={tags =>
                            tags.map(tag => (
                                <Tag color="blue" key={tag}>
                                    {tag}
                                </Tag>
                            ))
                        }
                        onCell={() => ({
                            "data-label": t("components.permissionPolicyList.fields.tags", {
                                ns: "userAuthorization"
                            })
                        })}
                    />

                    <Table.Column
                        align="right"
                        title={t("actions.action", {ns: "common"})}
                        render={item => (
                            <a onClick={() => handlePermissionPolicyDetailModalPress(item)}>
                                {t("actions.detail", {ns: "common"})}
                            </a>
                        )}
                        onCell={() => ({
                            "data-label": t("actions.action", {ns: "common"})
                        })}
                    />
                </ResponsiveDataTable>
            )}
        </React.Fragment>
    ));
};

export default AuthorizationManagement;
